import { useCallback, useMemo } from 'react';

import { ia, iaRa } from 'lib/helpers/utility';
import { useStates } from 'lib/hooks/queries/states/useStates';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';

import EmptyProviderState from './EmptyProviderState';
import { getProviderColumns } from './lib/providerColumns';
import { PROVIDER_FILTERS } from './lib/providerFilters';

const ProvidersTable = ({
  showStates,
  addProvider,
  providers,
  onRemove,
  fieldName,
  addProviderButton,
  setSelectedRows,
  className = 'h-[400px]',
  style,
  oppositeStateMap,
  onStatesChange
}) => {
  const isStatesEnabled = Boolean(showStates && !addProvider);
  const { data: statesData } = useStates({ options: { enabled: isStatesEnabled } });

  const isInNetwork = fieldName === 'in_network_providers';

  const stateOptions = useMemo(() => {
    return iaRa(statesData?.states).map((s) => ({ value: s.id, label: s.name }));
  }, [statesData]);

  const defaultColumns = getProviderColumns({
    showStates: isStatesEnabled && ia(stateOptions),
    enableSelection: addProvider,
    onRemove,
    stateOptions,
    oppositeStateMap,
    onStatesChange,
    isInNetwork
  });

  const handleSelect = (selectedNodes) => {
    if (!setSelectedRows) return;
    setSelectedRows(selectedNodes.map((node) => node?.data).filter(Boolean));
  };

  const getRowId = useCallback((params) => params.data?.id ?? params.data?.value, []);

  const noRowsOverlayComponent = useCallback(
    () => (
      <EmptyProviderState
        addProvider={addProvider}
        isInNetwork={isInNetwork}
        addProviderButton={addProviderButton}
      />
    ),
    [addProvider, isInNetwork, addProviderButton]
  );

  const useProvidersTableData = ({ params }) => {
    let filteredProviders = providers;

    if (params?.filters) {
      filteredProviders = filteredProviders.filter((provider) => {
        return Object.entries(params.filters).every(([column, value]) => {
          return value.every(({ formula, values }) => {
            const isContains = formula === 'contains';
            const providerValue = String(provider?.[column] ?? '').toLowerCase();
            const filterValue = String(values ?? '').toLowerCase();
            const includesValue = providerValue.includes(filterValue);

            return isContains ? includesValue : !includesValue;
          });
        });
      });
    }

    const data = { providers: filteredProviders, count: filteredProviders.length };

    return { data };
  };

  return (
    <div className={className} style={style}>
      <AgTablePage
        noHeader
        noFilterActions
        key={defaultColumns.length}
        name={addProvider ? 'add-network-providers' : 'payer-network-providers'}
        queryKey="providers"
        suppressMovableColumns={!addProvider}
        suppressDragLeaveHidesColumns={!addProvider}
        defaultColDef={{ suppressMenu: !addProvider }}
        noDisplayButton={!addProvider}
        useData={useProvidersTableData}
        defaultColumns={defaultColumns}
        defaultFilters={addProvider ? PROVIDER_FILTERS : {}}
        onSelect={handleSelect}
        noRowsOverlayComponent={noRowsOverlayComponent}
        rowSelection="multiple"
        suppressRowClickSelection={addProvider}
        getRowId={getRowId}
      />
    </div>
  );
};

export default ProvidersTable;
